import { Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import type { CSSProperties, ReactNode } from "react";

import type { TargetListItem, TargetWriteBody } from "../../api/targets";

interface TargetDrawerProps {
  target: TargetListItem;
  onClose: () => void;
  onSave: (body: Partial<TargetWriteBody>) => void;
  onRequestDelete: () => void;
  renderFooter: (actions: { onCancel: () => void; onSave: () => void }) => ReactNode;
}

export function TargetDrawer({ target, onClose, onSave, onRequestDelete, renderFooter }: TargetDrawerProps) {
  const [fullName, setFullName] = useState(target.full_name);
  const [firstName, setFirstName] = useState(target.first_name ?? "");
  const [lastName, setLastName] = useState(target.last_name ?? "");
  const [phone, setPhone] = useState(target.primary_phone_e164 ?? "");
  const [email, setEmail] = useState(target.primary_email ?? "");
  const [role, setRole] = useState(target.role ?? "");
  const [department, setDepartment] = useState(target.department ?? "");

  // Reset form when a different target is selected
  useEffect(() => {
    setFullName(target.full_name);
    setFirstName(target.first_name ?? "");
    setLastName(target.last_name ?? "");
    setPhone(target.primary_phone_e164 ?? "");
    setEmail(target.primary_email ?? "");
    setRole(target.role ?? "");
    setDepartment(target.department ?? "");
  }, [target.target_id]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleSave() {
    onSave({
      full_name: fullName,
      first_name: firstName || null,
      last_name: lastName || null,
      primary_phone_e164: phone || null,
      primary_email: email || null,
      role: role || null,
      department: department || null,
    });
  }

  return (
    <>
      {/* Form fields */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <DrawerField label="Full name" value={fullName} onChange={setFullName} />
        <DrawerField label="First name" value={firstName} onChange={setFirstName} />
        <DrawerField label="Last name" value={lastName} onChange={setLastName} />
        <DrawerField label="Phone (E.164)" value={phone} onChange={setPhone} mono />
        <DrawerField label="Email" value={email} onChange={setEmail} mono />
        <DrawerField label="Role" value={role} onChange={setRole} />
        <DrawerField label="Department" value={department} onChange={setDepartment} />
        <DrawerReadonly label="Target ID" value={target.target_id} />

        {/* Group membership (read-only here; manage per-group on the Group page) */}
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label style={fieldLabel()}>Groups</label>
          {target.group_names && target.group_names.length > 0 ? (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {target.group_names.map((n) => (
                <span
                  key={n}
                  style={{
                    background: "var(--bg-tertiary)",
                    border: "1px solid var(--border-subtle)",
                    borderRadius: 4,
                    padding: "2px 8px",
                    fontSize: 11,
                    color: "var(--text-secondary)",
                  }}
                >
                  {n}
                </span>
              ))}
            </div>
          ) : (
            <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
              Not in any group
            </span>
          )}
        </div>

        {/* Destructive: delete this target entirely */}
        <button
          onClick={onRequestDelete}
          style={{
            marginTop: 4,
            alignSelf: "flex-start",
            background: "transparent",
            border: "1px solid var(--error)",
            color: "var(--error)",
            borderRadius: 6,
            padding: "6px 12px",
            fontSize: 11.5,
            fontWeight: 600,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Trash2 size={13} /> Delete target
        </button>
      </div>
      {renderFooter({ onCancel: onClose, onSave: handleSave })}
    </>
  );
}

function DrawerField({
  label,
  value,
  onChange,
  mono,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  mono?: boolean;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={fieldLabel()}>{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          background: "var(--bg-input)",
          border: "1px solid var(--border)",
          borderRadius: 8,
          padding: "7px 10px",
          color: "var(--text-primary)",
          fontSize: 11.5,
          fontFamily: mono ? "var(--font-mono)" : "inherit",
          outline: "none",
          width: "100%",
          boxSizing: "border-box",
        }}
      />
    </div>
  );
}

function DrawerReadonly({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={fieldLabel()}>{label}</label>
      <input
        readOnly
        value={value}
        style={{
          background: "var(--bg-input)",
          border: "1px solid var(--border)",
          borderRadius: 8,
          padding: "7px 10px",
          color: "var(--text-muted)",
          fontFamily: "var(--font-mono)",
          fontSize: 10.5,
          outline: "none",
          cursor: "default",
          width: "100%",
          boxSizing: "border-box",
        }}
      />
    </div>
  );
}

function fieldLabel(): CSSProperties {
  return {
    fontSize: 9.5,
    textTransform: "uppercase",
    letterSpacing: 0.6,
    color: "var(--text-muted)",
    fontWeight: 600,
  };
}
